import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import { useWrkthruAssistantLauncher } from '@/features/ai/use-wrkthru-assistant-launcher';
import { WrkthruLoading } from './wrkthru-loading';
import { WrkthruNotice } from './wrkthru-notice';
import type { DailyBrief } from 'shared/src/trpc-types/daily-brief-trpc-types';
import type { WrkthruItemType } from 'shared/src/wrkthru/completions-types';
import type { WrkthruIssue, WrkthruTask } from 'shared/src/wrkthru/types';
import { buildWrkthruItSeed } from 'shared/src/wrkthru/wrkthru-it-seed';
import { dailyBriefToWrkthruPayload } from './daily-brief-adapter';
import { TodaysWrkPage } from './todays-wrk-page';
import { WrkthruSwitcher } from './wrkthru-switcher';
import type { ViewAs } from 'shared/src/trpc-types/daily-brief-trpc-types';
import './wrkthru.css';

/**
 * Wrkthru route container. 'Wrkthru_ it' on an Issue opens the Assistant
 * seeded with that Issue; on a Task it still routes to the related Campaign
 * (deliberate interim inconsistency while the Task flow is rethought).
 */
export function WrkthruPage() {
  const navigate = useNavigate();
  const launchWrkthruIt = useWrkthruAssistantLauncher();
  // `?refresh=1` evicts the server's per-day prioritizer cache and re-runs the
  // model — an operator/dev escape hatch, not part of normal navigation.
  const [searchParams] = useSearchParams();
  const forceRefresh = searchParams.get('refresh') !== null;
  // Undefined = the operator's own brief. Part of the query input, so switching
  // targets refetches in place.
  const [viewAs, setViewAs] = useState<ViewAs | undefined>(undefined);
  const { data, isPending, isError } = trpc.dailyBrief.useQuery(
    {
      ...(forceRefresh ? { forceRefresh: true } : {}),
      ...(viewAs ? { viewAs } : {})
    },
    {
      // The brief is a point-in-time read — don't silently re-run it under the
      // operator while they work their day.
      refetchOnWindowFocus: false
    }
  );

  // The switcher must survive the loading/error/empty states below, or a slow
  // or bad target would strand the operator with no way to reset the selection.
  const switcher = <WrkthruSwitcher value={viewAs} onChange={setViewAs} />;

  const utils = trpc.useUtils();
  const { data: completions } = trpc.wrkthruCompletions.useQuery();
  // A completion toggle invalidates only this cheap query — never the expensive
  // dailyBrief, which re-runs Cube/Zoho/LLM.
  const completionMutationOptions = {
    onSuccess: () => void utils.wrkthruCompletions.invalidate(),
    onError: () => showErrorToast()
  };
  const markWrkthruItemDone = trpc.markWrkthruItemDone.useMutation(completionMutationOptions);
  const unmarkWrkthruItemDone = trpc.unmarkWrkthruItemDone.useMutation(completionMutationOptions);

  const doneIssueIds = React.useMemo(
    () => new Set(completions?.doneIssueIds ?? []),
    [completions?.doneIssueIds]
  );
  const doneTaskIds = React.useMemo(
    () => new Set(completions?.doneTaskIds ?? []),
    [completions?.doneTaskIds]
  );

  if (isPending) {
    return (
      <div className="mx-auto flex h-full max-w-4xl flex-col gap-5 p-6">
        {switcher}
        <div className="flex flex-1 items-center justify-center">
          <WrkthruLoading />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="mx-auto max-w-4xl space-y-5 p-6">
        {switcher}
        <WrkthruNotice title="Couldn’t load your Wrkthru" role="alert">
          Something went wrong loading your day. Please try again shortly.
        </WrkthruNotice>
      </div>
    );
  }

  const brief = data as DailyBrief;

  const payload = dailyBriefToWrkthruPayload(brief, todayIso());

  const handleWrkthruIt = (target: WrkthruIssue | WrkthruTask) => {
    if ('entityHref' in target) {
      launchWrkthruIt(buildWrkthruItSeed(target));
    } else if (target.relatedCampaignHref) {
      navigate(target.relatedCampaignHref);
    }
  };

  const handleMarkDone = (itemType: WrkthruItemType, itemId: string) => {
    markWrkthruItemDone.mutate({ itemType, itemId });
  };
  const handleUndone = (itemType: WrkthruItemType, itemId: string) => {
    unmarkWrkthruItemDone.mutate({ itemType, itemId });
  };

  return (
    <>
      {/* Unlinked users see the org-wide Issue list; a banner explains the linkage state. */}
      {brief.unlinked && (
        <div className="mx-auto max-w-4xl px-6 pt-6" role="status">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Your account isn&apos;t linked to a Zoho user yet — showing all issues; tasks will
            appear once linked.
          </div>
        </div>
      )}
      <TodaysWrkPage
        // Remount per target so done-marks and the active tab never carry across briefs.
        key={briefKey(viewAs)}
        payload={payload}
        onWrkthruIt={handleWrkthruIt}
        doneIssueIds={doneIssueIds}
        doneTaskIds={doneTaskIds}
        onMarkDone={handleMarkDone}
        onUndone={handleUndone}
        switcher={switcher}
      />
    </>
  );
}

// Format must match how WrkthruHeader parses the date.
function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function briefKey(viewAs: ViewAs | undefined): string {
  if (!viewAs) return 'me';
  return viewAs.kind === 'user' ? `user:${viewAs.userId}` : `team:${viewAs.teamId}`;
}
